'use client';

import { Alert, Stack, Typography } from '@mui/material';
import { RdsCdcCheckScope, SqlSetupCheckStatus } from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { MainDialog } from '@/components/dialogs/mainDialog';
import { TemporalWorkflowLink } from '@/components/shared/temporalWorkflowLink';
import { useIsDevModeEnabled } from '@/contexts/useIsDevModeEnabled';
import { useTemporalWorkflowUrl } from '@/utils/temporalLink';
import { ParameterTable } from './parameterTable';
import { CheckingIndicator, stepFooterButtons, type StepDialogFadeProps } from './stepCheckControls';
import { useCdcCheckRun } from '../useCdcCheckRun';
import { useRdsCdcStatus } from '../useRdsCdcStatus';
const parameterGroupConsoleUrl = (region: string, parameterGroupName: string) => `https://${region}.console.aws.amazon.com/rds/home?region=${region}#parameter-group-details:ids=${parameterGroupName}`;
export const DatabaseParametersDialog = ({
  resourceId,
  region,
  onBack,
  onClose,
  ...fadeProps
}: {
  resourceId: string;
  region: string;
  onBack: () => void;
  onClose: () => void;
} & StepDialogFadeProps) => {
  const isDevMode = useIsDevModeEnabled();
  const temporalWorkflowUrl = useTemporalWorkflowUrl();
  const {
    isLoading,
    refresh,
    parameterChecks,
    isParametersStepDone,
    isPrivateLinkActive
  } = useRdsCdcStatus(resourceId);
  // Two of the four parameters describe the running engine rather than the requested configuration, so
  // the parameter group alone cannot settle them. This is the check that reads the engine, and it is
  // scoped to that reading: a failure to reach a rebooting instance must not retract the SQL step,
  // which is exactly when the customer comes back to this one.
  const check = useCdcCheckRun(resourceId, RdsCdcCheckScope.RDS_CDC_CHECK_SCOPE_PARAMETERS_AND_SLOTS);
  const parameterGroupName = parameterChecks[0]?.parameterGroupName ?? '';
  const isCheckFailed = check.terminalStatus === SqlSetupCheckStatus.SQL_SETUP_CHECK_STATUS_FAILED;

  // The parameter verdicts are the AWS parameter-group scan joined with the engine reading, and only
  // the heavy prerequisites key carries the join. One refresh when the reading lands republishes it to
  // this dialog, the hub, and the Backup details row.
  //
  // isSettling keeps the step busy across that refresh. The run's verdict arrives before the join is
  // re-fetched, so releasing on the verdict alone would drop the progress row while the table below it
  // still showed the previous reading.
  const [isSettling, setIsSettling] = useState(false);
  useEffect(() => {
    if (!check.hasVerdictForThisRun) {
      return;
    }
    let active = true;
    setIsSettling(true);
    const settle = async () => {
      try {
        await refresh();
      } catch {
        // The key keeps its last good value and the hub re-polls, so a failed refresh leaves the
        // table as it was rather than blanking it.
      } finally {
        if (active) {
          setIsSettling(false);
        }
      }
    };
    void settle();
    return () => {
      active = false;
    };
  }, [check.hasVerdictForThisRun, refresh]);
  const isChecking = check.isVerifying || isSettling;
  return <MainDialog isLoading={isLoading} scrollableBody {...fadeProps} header={{
    title: 'Set database parameters',
    subtitle: "These parameters must be set on the instance's parameter group before Eon can read changes.",
    onBack,
    onClose
  }} footer={{
    ...stepFooterButtons({
      isDone: isParametersStepDone,
      isBusy: isChecking || check.isLoading,
      // Reading the engine needs a connection to it, so the blocker sits on the button while the
      // parameter group itself stays readable.
      blockedReason: isPrivateLinkActive ? undefined : 'Available once AWS PrivateLink is active',
      testIdPrefix: 'rds-cdc-parameters',
      onContinue: onBack,
      onCheckAndContinue: () => void check.start(),
      onVerifyAgain: () => void check.start()
    }),
    children: !!region && !!parameterGroupName && <ButtonWithIcon variant='text' icon='material-symbols-open-in-new' iconPosition='right' text='Open parameter group in AWS Console' onClick={() => window.open(parameterGroupConsoleUrl(region, parameterGroupName), '_blank', 'noopener')} />
  }}>
      <Stack sx={{
      gap: '16px',
      paddingX: '40px'
    }}>
        {!isChecking && parameterChecks.length === 0 && <Alert severity='error'>
            Eon couldn&apos;t read the instance&apos;s parameter group, so there
            is nothing to check yet. Refresh and try again, or contact support
            if it keeps happening.
          </Alert>}
        {parameterChecks.length > 0 && <>
            {!!parameterGroupName && <Stack direction='row' sx={{
          gap: '8px',
          alignItems: 'baseline'
        }}>
                <Typography variant='subtitle1Strong'>
                  Parameter group:
                </Typography>
                <Typography variant='body1'>
                  <code>{parameterGroupName}</code>
                </Typography>
              </Stack>}
            <ParameterTable params={parameterChecks} />
          </>}
        {isChecking && <CheckingIndicator text='Checking database parameters. This might take a few minutes.' />}
        {!isChecking && isParametersStepDone && <Alert severity='success'>All required parameters are set.</Alert>}
        {!isChecking && isCheckFailed && <Alert severity='error'>
            {check.verdict?.error || 'Eon could not read the settings your database is running with. Try again, or contact support if it keeps failing.'}
          </Alert>}
        {isDevMode && <TemporalWorkflowLink unavailableReason='Developer mode: Run verification first to see Workflow execution' href={check.workflow && temporalWorkflowUrl(check.workflow.namespace, check.workflow.id)} />}
      </Stack>
    </MainDialog>;
};